import { Label } from '@/components/ui/label';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { OptionButtonGroup } from '@/components/ui/option-button-group';
import { useI18nContext } from '@/i18n/i18n-react';
import { validateLinkRule } from '@/lib/campaign-fields';
import { mobileLayer } from '@/lib/mobile-layer';
import type { StoreLinkMode } from '@/lib/store-link';
import { cn } from '@/lib/utils';

const ERROR_ID = 'composer-link-error';
const DESCRIPTION_ID = 'composer-link-description';

/**
 * "Ao tocar na notificação": the store's home page (nothing to type) or
 * another page of it, typed as just its path next to the fixed domain. A
 * whole link of the store pasted in (or left in the field) becomes its path;
 * a link of another site is named as such.
 */
export function StoreLinkField({
  mode,
  path,
  link,
  domain,
  onModeChange,
  onPathChange,
}: {
  mode: StoreLinkMode;
  path: string;
  link: string;
  domain: string;
  onModeChange(mode: StoreLinkMode): void;
  onPathChange(path: string, normalize?: boolean): void;
}) {
  const { LL } = useI18nContext();

  if (domain.trim() === '') {
    return (
      <p className="text-sm text-muted-foreground">
        {LL.campaignComposer.link.noDomain()}
      </p>
    );
  }

  const error = (() => {
    if (mode !== 'page' || path.trim() === '') return null;
    switch (validateLinkRule(link, domain)) {
      case 'linkNotFromStore': {
        let host = link;
        try {
          host = new URL(link).hostname;
        } catch {
          // Keep the link as typed.
        }
        return LL.campaignComposer.link.otherSite({ host, domain });
      }
      case 'linkInvalid':
        return LL.campaignComposer.link.invalid();
      default:
        return null;
    }
  })();

  return (
    <div className="space-y-3">
      {/* Same buttons as "Quando enviar": both are "pick one" choices. */}
      <OptionButtonGroup
        value={mode}
        options={[
          { value: 'home', label: LL.campaignComposer.link.home() },
          { value: 'page', label: LL.campaignComposer.link.page() },
        ]}
        onChange={onModeChange}
        ariaLabel={LL.campaignComposer.link.modeLabel()}
        className="[&_button]:max-md:min-h-touch"
      />
      {mode === 'home' ? (
        <p className="text-sm text-muted-foreground">
          {LL.campaignComposer.link.homeDescription({ domain })}
        </p>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="composer-link">
            {LL.campaignComposer.link.pageLabel()}
          </Label>
          <p
            aria-hidden
            className="truncate text-sm text-muted-foreground md:hidden"
          >
            {domain}/
          </p>
          {/* `h-auto` on phones: the touch-sized input is taller than the
              group's default height and would hide its side borders. */}
          <InputGroup
            className={cn(
              'max-md:h-auto',
              error && 'border-destructive',
              mobileLayer.input
            )}
          >
            {/* Beside the path from 768px; above the field on phones (see
                the line before the group), where a long domain
                ("loja.myshopify.com/") would squeeze the path. */}
            <InputGroupAddon className="pr-0 font-normal max-md:hidden">
              {domain}/
            </InputGroupAddon>
            <InputGroupInput
              id="composer-link"
              value={path}
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder={LL.campaignComposer.link.pagePlaceholder()}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? ERROR_ID : DESCRIPTION_ID}
              onChange={(e) => onPathChange(e.target.value)}
              onBlur={(e) => onPathChange(e.target.value, true)}
              onPaste={(e) => {
                const input = e.currentTarget;
                const replacesAll =
                  input.value === '' ||
                  (input.selectionStart === 0 &&
                    input.selectionEnd === input.value.length);
                if (!replacesAll) return;
                e.preventDefault();
                onPathChange(e.clipboardData.getData('text'), true);
              }}
              className="md:pl-1"
            />
          </InputGroup>
          {error ? (
            <p id={ERROR_ID} className="text-sm text-destructive">
              {error}
            </p>
          ) : (
            <p id={DESCRIPTION_ID} className="text-sm text-muted-foreground">
              {LL.campaignComposer.link.pageDescription()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
